import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditLogSearchCondition } from '../types/auditLog';
import { useExportAuditLog } from '../hooks/useExportAuditLog';

interface ExportButtonProps {
  filters: AuditLogSearchCondition;
}

/** Toast 알림 컴포넌트 */
function ExportToast({
  message,
  hint,
  tone,
  onDismiss,
}: {
  message: string;
  hint?: string;
  tone: 'success' | 'error' | 'info';
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const borderColor =
    tone === 'success'
      ? 'var(--color-success)'
      : tone === 'error'
        ? 'var(--color-danger)'
        : 'var(--color-info)';

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 'var(--spacing-6)',
        right: 'var(--spacing-6)',
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-3)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        background: 'var(--color-surface-elevated)',
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-md)',
        maxWidth: '420px',
        zIndex: 300,
      }}
    >
      <span style={{ color: borderColor, fontSize: 'var(--font-size-lg)' }}>
        {tone === 'success' ? '✓' : tone === 'error' ? '✕' : 'ℹ'}
      </span>
      <div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {message}
        </div>
        {hint && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--spacing-1)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {hint}
          </div>
        )}
        {tone === 'info' && (
          <div
            style={{
              marginTop: 'var(--spacing-2)',
              height: 4,
              background: 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-full)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: '65%',
                background: 'var(--color-info)',
                borderRadius: 'var(--radius-full)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label={t('auditLog.export.toastClose')}
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          padding: 0,
          marginLeft: 'var(--spacing-2)',
        }}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * ExportButton — 감사 로그 엑셀 다운로드 버튼
 * Toast: 시작(info) / 성공(success) / 오류(error)
 */
export function ExportButton({ filters }: ExportButtonProps) {
  const { t, i18n } = useTranslation();
  const { doExport, status, errorCode, isPending } = useExportAuditLog();
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    if (status === 'loading' || status === 'success' || status === 'error') {
      setToastVisible(true);
    }
    if (status === 'success') {
      const t = setTimeout(() => setToastVisible(false), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status]);

  const toastTone =
    status === 'success' ? 'success' : status === 'error' ? 'error' : 'info';
  const toastMessage =
    status === 'loading'
      ? t('auditLog.toast.exportStarted')
      : status === 'success'
        ? t('auditLog.export.downloadComplete')
        : errorCode === 'AUDIT-422-001'
          ? t('auditLog.error.AUDIT-422-001')
          : errorCode === 'AUTH-403-001'
            ? t('auditLog.export.noPermission')
            : t('auditLog.export.downloadFailed');

  return (
    <>
      <button
        type="button"
        aria-label={t('auditLog.list.button.export')}
        disabled={isPending}
        onClick={() => void doExport(filters, i18n.language === 'en' ? 'en' : 'ko')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: isPending ? 'var(--color-surface-elevated)' : 'var(--color-brand-primary)',
          color: isPending ? 'var(--color-text-secondary)' : 'var(--color-text-inverse)',
          border: isPending ? '1px solid var(--color-surface-border)' : 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          cursor: isPending ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-family-sans)',
          opacity: isPending ? 0.7 : 1,
          transition: 'background 0.15s',
        }}
      >
        {isPending ? t('auditLog.export.downloading') : t('auditLog.list.button.export')}
      </button>

      {toastVisible && (
        <ExportToast
          message={toastMessage}
          tone={toastTone}
          onDismiss={() => setToastVisible(false)}
        />
      )}
    </>
  );
}
